import type { OnChangeHandlerType, BaseOptionType } from '.'
import type { GetContainerBorderColor, GetSingleValueColor } from './types'
import type {
  ActionMeta,
  GroupBase,
  OnChangeValue,
  OptionsOrGroups,
} from 'react-select'

export const getContainerBorderColor: GetContainerBorderColor = (
  menuIsOpen,
  error,
  theme
) => {
  const baseBorderColor = menuIsOpen
    ? theme.colors.second
    : theme.colors.crystalBay
  const errorBorderColor = menuIsOpen
    ? theme.colors.second
    : theme.colors.danger
  return error ? errorBorderColor : baseBorderColor
}

export const getSingleValueColor: GetSingleValueColor = (
  menuIsOpen,
  isDisabled,
  theme
) => {
  const slateGray = theme.colors.slateGray
  const color = menuIsOpen ? slateGray : theme.colors.sapphireDark
  return isDisabled ? slateGray : color
}

export const onChangeHandlerWrapper =
  <ValueType>(onChange: OnChangeHandlerType<ValueType>) =>
  <IsMulti extends boolean>(
    option: OnChangeValue<BaseOptionType<ValueType>, IsMulti>,
    actionMeta?: ActionMeta<BaseOptionType<ValueType>>
  ) => {
    let value: ValueType | ValueType[] | null =
      (option as BaseOptionType<ValueType>)?.value ?? null
    if (Array.isArray(option)) {
      value = option.map(({ value }) => value)
    }
    onChange(value, actionMeta)
  }

export const getSelectedValue = <ValueType>(
  options: OptionsOrGroups<
    BaseOptionType<ValueType>,
    GroupBase<BaseOptionType<ValueType>>
  >,
  value?: ValueType | ValueType[] | BaseOptionType<ValueType>
):
  | false
  | BaseOptionType<ValueType>
  | GroupBase<BaseOptionType<ValueType>>
  | (BaseOptionType<ValueType> | GroupBase<BaseOptionType<ValueType>>)[] => {
  if (value === null || value === undefined) {
    return false
  }

  if (Array.isArray(value)) {
    const valueSet = new Set(value)

    return options?.filter(
      (option) =>
        isBaseOptionType<ValueType>(option) && valueSet.has(option.value)
    )
  }

  return (
    options?.find((option) => {
      if (!isBaseOptionType<ValueType>(option)) {
        return false
      }

      const currentValue = isBaseOptionType<ValueType>(value)
        ? value.value
        : value

      return currentValue === option.value
    }) || false
  )
}

export function isBaseOptionType<T>(obj: any): obj is BaseOptionType<T> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'label' in obj &&
    typeof obj.label === 'string' &&
    'value' in obj
  )
}

export function isGroupBaseOptionType<T>(
  obj: any
): obj is GroupBase<BaseOptionType<T>> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'options' in obj &&
    Array.isArray(obj.options)
  )
}

export const getControlBorderRadius = (isMenuPlaceMentTop: boolean) =>
  isMenuPlaceMentTop ? '8px' : '8px 8px 0 0'
